import React, { useEffect, useState } from 'react';
import * as R from 'ramda';
import { usePlannerConfig } from 'contexts/plannerConfig';
import { useSelector } from 'react-redux';
import { getMarketProducts, getSelectedGroupId } from 'ducks/products';
import { useIntl } from 'react-intl';
import styled from 'styled-components';
import Box from 'ui/Box';
import { getActiveWidget, setActiveWidget } from 'ducks/mobile';
import { useCore } from 'contexts/core';
import media from 'ui/media';
import { useAppDispatch } from 'ducks/hooks';
import { getMenuTitle } from 'components/ProductSidebar/menus/utils/utils';
import { Tab } from '@ingka/tabs';
import CircleFilled from '@ingka/ssr-icon/paths/circle-filled';
import SkapaTabs from 'ui/Tabs/SkapaTabs';

const Widgets = styled.div`
  display: flex;
  height: 100%;
  width: 100%;
  align-items: center;
  justify-content: center;
`;

const WidgetWrapper = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  &[data-hidden='true'] {
    display: none;
  }
`;

const TabWrapper = styled(Box)`
  padding-left: 1.5rem;
  padding-right: 1.5rem;
  ${media.thinMobile`
    padding-left: 0.5rem;
    padding-right: 0.5rem;
  `}
`;

const StyledTab = styled(Tab)<{ fillcolor?: string }>`
  .svg-icon {
    fill: ${({ fillcolor }) => `${fillcolor ? fillcolor : '#9D9E9D'}`};
    border: ${({ fillcolor }) => `${fillcolor === '#FFFFFF' ? '1px solid #929292' : 'none'}`};
    border-radius: 100%;
  }
`;

const ProductMenuContent = () => {
  const selectedGroupId = useSelector(getSelectedGroupId);
  const activeWidget = useSelector(getActiveWidget);
  const dispatch = useAppDispatch();
  const coreApi = useCore();
  const { formatMessage } = useIntl();
  const {
    plannerConfig: { productGroups }
  } = usePlannerConfig();
  const [hiddenWidgets, setHiddenWidgets] = useState<string[]>([]);
  const products = useSelector(getMarketProducts);

  const selectedGroup = R.find(R.propEq('id', selectedGroupId), productGroups);
  const widgets = selectedGroup?.mainMenu.widgets;
  const filteredWidgets = widgets?.filter(w => (w.hidden ? !w.hidden(coreApi, products) : true));

  const onSelectTab = (id: string) => {
    dispatch(setActiveWidget(id));
  };

  useEffect(() => {
    if (!selectedGroupId) {
      dispatch(setActiveWidget(null));
    } else {
      const visibleWidgets = filteredWidgets?.filter(w => !hiddenWidgets.includes(w.id));
      const firstTab: string | null =
        (visibleWidgets && visibleWidgets.length > 0 && visibleWidgets[0]?.id) || null;

      if (activeWidget && visibleWidgets?.map(w => w.id).includes(activeWidget)) return;
      dispatch(setActiveWidget(firstTab));
    }
  }, [dispatch, widgets, selectedGroupId, hiddenWidgets, activeWidget, filteredWidgets]);

  const selectedWidgetTitleProp = widgets?.find(widget => widget.id === activeWidget)?.title;
  const selectedWidgetTitle =
    typeof selectedWidgetTitleProp === 'function'
      ? selectedWidgetTitleProp(coreApi)
      : selectedWidgetTitleProp;

  const selectedWidgetDisable = widgets?.find(widget => widget.id === activeWidget)?.disabled;

  const hideTabs = (widgets?.length === 1 && !widgets[0].title) || filteredWidgets?.length === 1;

  return (
    <>
      {!hideTabs && (
        <TabWrapper>
          {activeWidget && filteredWidgets && (
            <SkapaTabs
              defaultActiveTab={filteredWidgets[0].id}
              onTabChanged={(tab: string) => (setActiveWidget(tab), onSelectTab(tab))}
              tabs={filteredWidgets
                ?.filter(widget => !hiddenWidgets.includes(widget.id))
                .map(widget => (
                  <StyledTab
                    key={widget.id}
                    tabPanelId={widget.id}
                    data-testid={`mobile-menu-tab-${widget.id}`}
                    text={getMenuTitle(widget.title, undefined, formatMessage, coreApi)}
                    ssrIcon={widget.iconColor ? CircleFilled : undefined}
                    fillcolor={widget.iconColor && widget.iconColor}
                  />
                ))}
            />
          )}
        </TabWrapper>
      )}
      <Widgets>
        {filteredWidgets?.map(({ id, component: Component }: { id: string; component: any }) => {
          return (
            <WidgetWrapper data-hidden={id !== activeWidget} key={id}>
              <Component
                key={id}
                id={id}
                title={selectedWidgetTitle}
                selected={id === activeWidget}
                disabled={selectedWidgetDisable}
                hideTab={(status: boolean) => {
                  if (status && !hiddenWidgets.includes(id)) {
                    setHiddenWidgets([...hiddenWidgets, id]);
                  } else if (!status && hiddenWidgets.includes(id)) {
                    setHiddenWidgets(hiddenWidgets.filter(w => w !== id));
                  }
                }}
              />
            </WidgetWrapper>
          );
        })}
      </Widgets>
    </>
  );
};

export default ProductMenuContent;
